import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from './AuthContext'
import { ChatService } from '../services/chat.service'
import { ChatRoom } from '../types/chat'

interface ChatContextType {
  // UI state
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  toggleChat: () => void
  isChatHidden: boolean // When true, chat toggle/container are not rendered (e.g. during full-screen modals)
  setChatHidden: (hidden: boolean) => void

  // Room state
  currentRoom: ChatRoom | null
  availableRooms: ChatRoom[]
  roomLoading: boolean
  roomError: string | null
  switchRoom: (roomId: string) => void
  refreshRooms: () => Promise<void>

  // Unread tracking
  unreadCount: number // Total unread across all rooms
  unreadCounts: Map<string, number> // Unread per room
  markAsRead: () => void
  refreshUnreadCounts: () => Promise<void>
  hasUnread: boolean // Quick check if any unread messages exist
  shouldPulse: boolean // Whether to show pulse animation (limited to 15 seconds)

  // Sound settings
  soundEnabled: boolean
  setSoundEnabled: (enabled: boolean) => void
  soundVolume: number // 0 to 1
  setSoundVolume: (volume: number) => void
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

export function useChatContext() {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider')
  }
  return context
}

interface ChatProviderProps {
  children: React.ReactNode
}

// Storage keys for sound preferences
const SOUND_ENABLED_KEY = 'chat_sound_enabled'
const SOUND_VOLUME_KEY = 'chat_sound_volume'
const DEFAULT_VOLUME = 0.5

// Notification sound (base64 encoded short beep - very subtle)
const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1sbHVtc3mFgoWLi4iFgn16eHl5eXyBhYaGhYSCgH59fHt8fX6AgYKDg4OCgYB/fn19fX5/gIGBgYGBgIB/fn5+fn5/f4CAgICAgIB/f35+fn5+f3+AgICAgICAf39/f39/f39/gICAgICAgH9/f39/f39/f4CAgICAgIB/f39/f39/f3+AgICAgICAf39/f39/f39/gICAgICAgH9/f39/f39/f4CAgICAgIB/f39/f39/f3+AgA=='

export function ChatProvider({ children }: ChatProviderProps) {
  const { user } = useAuth()

  // UI state
  const [isOpen, setIsOpen] = useState(false)
  const [isChatHidden, setIsChatHidden] = useState(false)

  // Room state
  const [currentRoom, setCurrentRoom] = useState<ChatRoom | null>(null)
  const [availableRooms, setAvailableRooms] = useState<ChatRoom[]>([])
  const [roomLoading, setRoomLoading] = useState(false)
  const [roomError, setRoomError] = useState<string | null>(null)

  // Unread tracking
  const [unreadCounts, setUnreadCounts] = useState<Map<string, number>>(new Map())
  const unreadPollingRef = useRef<NodeJS.Timeout | null>(null)
  const [shouldPulse, setShouldPulse] = useState(false)
  const previousUnreadCountRef = useRef<number>(0)

  // Sound settings - persisted to localStorage (default: on)
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SOUND_ENABLED_KEY)
      // Default to true if not set, otherwise use stored value
      return stored === null ? true : stored === 'true'
    }
    return true
  })
  const [soundVolume, setSoundVolumeState] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SOUND_VOLUME_KEY)
      if (stored !== null) {
        const parsed = parseFloat(stored)
        if (!isNaN(parsed) && parsed >= 0 && parsed <= 1) {
          return parsed
        }
      }
    }
    return DEFAULT_VOLUME
  })
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_URL)
    audioRef.current.volume = soundVolume
    return () => {
      audioRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update audio volume when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = soundVolume
    }
  }, [soundVolume])

  // Persist sound enabled setting
  const setSoundEnabled = useCallback((enabled: boolean) => {
    setSoundEnabledState(enabled)
    localStorage.setItem(SOUND_ENABLED_KEY, String(enabled))
  }, [])

  // Persist sound volume setting
  const setSoundVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume))
    setSoundVolumeState(clampedVolume)
    localStorage.setItem(SOUND_VOLUME_KEY, String(clampedVolume))
  }, [])

  // Calculate total unread count
  const unreadCount = Array.from(unreadCounts.values()).reduce((sum, count) => sum + count, 0)
  const hasUnread = unreadCount > 0

  // Handle pulse animation timing (15 seconds max)
  useEffect(() => {
    if (hasUnread && !isOpen) {
      // Start pulse when unread messages appear
      setShouldPulse(true)

      // Stop pulse after 15 seconds
      const timeout = setTimeout(() => {
        setShouldPulse(false)
      }, 15000)

      return () => {
        clearTimeout(timeout)
      }
    } else {
      // No unread or chat is open - stop pulse
      setShouldPulse(false)
    }
  }, [hasUnread, isOpen]) // Only depend on hasUnread and isOpen

  // Play sound when new messages arrive
  useEffect(() => {
    if (soundEnabled && unreadCount > previousUnreadCountRef.current && previousUnreadCountRef.current >= 0) {
      // New unread messages arrived - play sound
      if (audioRef.current) {
        audioRef.current.currentTime = 0
        audioRef.current.play().catch(() => {
          // Ignore autoplay errors (browser may block)
        })
      }
    }
    previousUnreadCountRef.current = unreadCount
  }, [unreadCount, soundEnabled])

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  const setChatHidden = useCallback((hidden: boolean) => {
    setIsChatHidden(hidden)
  }, [])

  // Refresh unread counts from the server
  const refreshUnreadCounts = useCallback(async () => {
    if (!user) return

    try {
      const counts = await ChatService.getUnreadCounts()
      setUnreadCounts(counts)
    } catch (err) {
      console.error('Failed to refresh unread counts:', err)
    }
  }, [user])

  // Mark current room as read
  const markAsRead = useCallback(async () => {
    if (!currentRoom) return

    try {
      await ChatService.markRoomAsRead(currentRoom.id)
      // Update local state immediately
      setUnreadCounts((prev) => {
        const next = new Map(prev)
        next.delete(currentRoom.id)
        return next
      })
    } catch (err) {
      console.error('Failed to mark room as read:', err)
    }
  }, [currentRoom])

  const switchRoom = useCallback((roomId: string) => {
    const room = availableRooms.find((r) => r.id === roomId)
    if (room) {
      setCurrentRoom(room)
    }
  }, [availableRooms])

  const refreshRooms = useCallback(async () => {
    if (!user) return

    try {
      const rooms = await ChatService.getUserRooms()
      setAvailableRooms(rooms)

      // If current room is no longer available, switch to default
      if (currentRoom && !rooms.find((r) => r.id === currentRoom.id)) {
        const defaultRoom = rooms.find((r) => r.is_default)
        setCurrentRoom(defaultRoom || rooms[0] || null)
      }
    } catch (err) {
      console.error('Failed to refresh rooms:', err)
    }
  }, [user, currentRoom])

  // Load rooms when user is authenticated
  useEffect(() => {
    if (!user) {
      setCurrentRoom(null)
      setAvailableRooms([])
      setRoomError(null)
      return
    }

    const loadRooms = async () => {
      setRoomLoading(true)
      setRoomError(null)

      try {
        const rooms = await ChatService.getUserRooms()
        setAvailableRooms(rooms)

        // Set default room as current
        const defaultRoom = rooms.find((r) => r.is_default)
        setCurrentRoom(defaultRoom || rooms[0] || null)
      } catch (err) {
        setRoomError(err instanceof Error ? err.message : 'Failed to load chat rooms')
        setCurrentRoom(null)
      } finally {
        setRoomLoading(false)
      }
    }

    loadRooms()
  }, [user])

  // Poll for unread counts (every 5 seconds when chat is closed, every 10 seconds when open)
  useEffect(() => {
    if (!user) {
      setUnreadCounts(new Map())
      return
    }

    // Initial fetch
    refreshUnreadCounts()

    // Set up polling
    const pollInterval = isOpen ? 10000 : 5000 // Poll less frequently when chat is open
    unreadPollingRef.current = setInterval(refreshUnreadCounts, pollInterval)

    return () => {
      if (unreadPollingRef.current) {
        clearInterval(unreadPollingRef.current)
      }
    }
  }, [user, isOpen, refreshUnreadCounts])

  // Mark room as read when viewing it (with a small delay to ensure messages are loaded)
  useEffect(() => {
    if (!isOpen || !currentRoom) return

    // Mark as read after a short delay
    const timeout = setTimeout(() => {
      markAsRead()
    }, 1000)

    return () => clearTimeout(timeout)
  }, [isOpen, currentRoom, markAsRead])

  // Clear state on logout
  useEffect(() => {
    if (!user) {
      setIsOpen(false)
      setUnreadCounts(new Map())
    }
  }, [user])

  const value: ChatContextType = {
    isOpen,
    setIsOpen,
    toggleChat,
    isChatHidden,
    setChatHidden,
    currentRoom,
    availableRooms,
    roomLoading,
    roomError,
    switchRoom,
    refreshRooms,
    unreadCount,
    unreadCounts,
    markAsRead,
    refreshUnreadCounts,
    hasUnread,
    shouldPulse,
    soundEnabled,
    setSoundEnabled,
    soundVolume,
    setSoundVolume,
  }

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

